import React, { useState } from 'react';
import { X, Upload, User } from 'lucide-react';
import { trackEngagement, trackError } from '../Utils/analytics';
import '../DeliveryPartnerModal/DeliveryPartnerModal.css';

interface DeliveryPartnerModalProps {
  onClose: () => void;
}

interface DeliveryPartnerData {
  fullName: string;
  email: string;
  phone: string;
  profileImage: File | null;
}

const DeliveryPartnerModal: React.FC<DeliveryPartnerModalProps> = ({ onClose }) => {
  const [formData, setFormData] = useState<DeliveryPartnerData>({
    fullName: '',
    email: '',
    phone: '',
    profileImage: null
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleInputChange = (field: keyof DeliveryPartnerData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (file: File) => {
    setFormData(prev => ({
      ...prev,
      profileImage: file
    }));
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone && !/^[+]?[0-9\s\-()]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      trackError('delivery_partner_validation_failed', 'delivery_partner_modal');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      
      // Add text fields
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);

      // Add profile image
      if (formData.profileImage) {
        submitData.append('profileImage', formData.profileImage);
      }

      const response = await fetch('https://api.homecookt.com/delivery/register', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('userID')}`,
        },
        body: submitData,
      });

      if (response.ok) {
        onClose();
        alert('Application submitted successfully! We\'ll review your application and get back to you within 24 hours.');
        trackEngagement('delivery_partner_application_submitted');
      } else {
        const data = await response.json();
        setErrors({ general: data.message || 'Failed to submit application' });
        trackError('delivery_partner_api_failed', 'delivery_partner_modal');
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please try again.' });
      trackError('delivery_partner_network_error', 'delivery_partner_modal');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="delivery-partner-overlay" onClick={onClose}>
      <div className="delivery-partner-modal" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="modal-header">
          <h2>Become a Delivery Partner</h2>
        </div>

        <div className="modal-content">
          <div className="step-content">
            <h3>Personal Information</h3>
            
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                id="fullName"
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className={errors.fullName ? 'error' : ''}
                placeholder="Enter your full name"
              />
              {errors.fullName && <div className="error-message">{errors.fullName}</div>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'error' : ''}
                  placeholder="your@email.com"
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={errors.phone ? 'error' : ''}
                  placeholder="+44 7XXX XXXXXX"
                />
                {errors.phone && <div className="error-message">{errors.phone}</div>}
              </div>
            </div>

            <div className="form-group">
              <label>Profile Photo</label>
              <div className="image-upload-section">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                  className="hidden-input"
                  id="profileImage"
                />
                <label htmlFor="profileImage" className="upload-button">
                  <Upload size={20} />
                  {formData.profileImage ? 'Change Photo' : 'Upload Photo'}
                </label>
                
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Profile preview" />
                  </div>
                )}
                
                {!imagePreview && (
                  <div className="image-placeholder">
                    <User size={40} />
                    <span>No photo selected</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {errors.general && <div className="error-message general-error">{errors.general}</div>}

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryPartnerModal;